/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  SectionHeader,
  LoadingSpinner,
  LoadingContainer,
  Modal,
} from 'components'
import * as Types from 'types'
import { ProgramBlade, CopyProgramModal } from '../components'
import * as flashActions from 'redux-flash'
import { waitFor, onMount } from 'lp-hoc'
import * as apiActions from 'api-actions'
import { selectors as globalSelectors } from 'global-reducer'
import { isEmpty } from 'lodash'
import { selectors as apiSelectors } from 'lp-redux-api'

const propTypes = {
  updateArtistForSchoolYear: PropTypes.func.isRequired,
  setShowAddSchoolPartnershipModal: PropTypes.func.isRequired,
  setShowAddProgramModal: PropTypes.func.isRequired,
  showAddProgramModal: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  previousPrograms: PropTypes.arrayOf(Types.program),
  schoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  visibleSchoolYearId: PropTypes.number.isRequired,
  programsForSchoolYear: PropTypes.arrayOf(Types.taProgram),
}

const defaultProps = {
  programsForSchoolYear: [],
  disabled: false,
  previousPrograms: [],
}

function ProgramTable({
  disabled,
  updateArtistForSchoolYear,
  schoolYears,
  programsForSchoolYear,
  previousPrograms,
  artistId,
  setArtist,
  grades,
  outcomeTypes,
  programTypes,
  approaches,
  disciplines,
  isEditable,
  isLoading,
  setShowAddSchoolPartnershipModal,
  setShowAddProgramModal,
  showAddProgramModal,
  languages,
}) {
  const [showCopyProgramsModal, setShowCopyProgramsModal] = useState(false)
  const [newProgram, setNewProgram] = useState(null)
  const [showProgramConfirmationModal, setShowProgramConfirmationModal] =
    useState(false)
  const [showAssociatedSchoolModal, setShowAssociatedSchoolModal] =
    useState(false)

  return (
    <div>
      <SectionHeader>
        <h2>What programs do you have?</h2>
        {!disabled && (
          <div className="button-area many-buttons">
            <button
              type="button"
              onClick={() => setShowAddProgramModal(true)}
              className="button-primary-outline"
            >
              Add a new program
            </button>
            <button
              type="button"
              onClick={() => setShowCopyProgramsModal(true)}
              className="button-primary-outline"
            >
              Copy program from other school year
            </button>
          </div>
        )}
        <div className="header-title">Programs</div>

        {!isEmpty(programsForSchoolYear) && (
          <div className="expandable-section program-expander">
            <LoadingContainer isLoading={isLoading}>
              {programsForSchoolYear.map((program) => (
                <ProgramBlade
                  key={program.id}
                  program={program}
                  onSubmit={(params) =>
                    updateArtistForSchoolYear({
                      id: artistId,
                      taPrograms: [params],
                    })
                  }
                  onSubmitSuccess={setArtist}
                  onDelete={() =>
                    updateArtistForSchoolYear({
                      id: artistId,
                      taPrograms: [{ ...program, _destroy: true }],
                    })
                  }
                  disabled={!isEditable}
                  grades={grades}
                  outcomeTypes={outcomeTypes}
                  programTypes={programTypes}
                  approachTypes={approaches}
                  disciplines={disciplines}
                  languages={languages}
                  artistId={artistId}
                />
              ))}
            </LoadingContainer>
          </div>
        )}
      </SectionHeader>
      {showAddProgramModal && isEditable && (
        <Modal onClose={() => setShowAddProgramModal(false)}>
          <ProgramBlade
            onSubmit={(params) => {
              setNewProgram(params)
              return updateArtistForSchoolYear({
                id: artistId,
                taPrograms: [params],
              })
            }}
            onSubmitSuccess={(artist) => {
              setShowAddProgramModal(false)
              setShowProgramConfirmationModal(true)
              return setArtist(artist)
            }}
            onDelete={() => setShowAddProgramModal(false)}
            grades={grades}
            outcomeTypes={outcomeTypes}
            programTypes={programTypes}
            approachTypes={approaches}
            disciplines={disciplines}
            languages={languages}
            artistId={artistId}
            isNewProgram
          />
        </Modal>
      )}
      {showCopyProgramsModal && (
        <CopyProgramModal
          onClose={() => setShowCopyProgramsModal(false)}
          programs={previousPrograms}
          schoolYears={schoolYears}
          onSubmit={({ programs }) =>
            updateArtistForSchoolYear({ id: artistId, taPrograms: programs })
          }
          onSubmitSuccess={(res) => {
            setShowCopyProgramsModal(false)
            return setArtist(res)
          }}
        />
      )}
      {newProgram && showProgramConfirmationModal && (
        <Modal
          onClose={() => {
            setNewProgram(null)
            setShowProgramConfirmationModal(false)
          }}
        >
          <div className="modal-title">Add New Program</div>
          <div className="card new-program-confirmation">
            <div>{newProgram.name}</div>
            <div className="saved-badge">Program saved</div>
          </div>
          <div className="program-buttons">
            <button
              className="button-primary"
              onClick={() => {
                setNewProgram(null)
                setShowProgramConfirmationModal(false)
                setShowAssociatedSchoolModal(true)
              }}
            >
              Save
            </button>
            <button
              className="button-primary-outline"
              onClick={() => {
                setNewProgram(null)
                setShowProgramConfirmationModal(false)
                setShowAddProgramModal(true)
              }}
            >
              Add another program
            </button>
          </div>
        </Modal>
      )}
      {showAssociatedSchoolModal && (
        <Modal onClose={() => setShowAssociatedSchoolModal(false)}>
          <div className="modal-title">Are you associated with a school?</div>
          <div className="program-buttons">
            <button
              className="button-primary"
              onClick={() => {
                setShowAssociatedSchoolModal(false)
              }}
            >
              No
            </button>
            <button
              className="button-primary"
              onClick={() => {
                setShowAssociatedSchoolModal(false)
                setShowAddSchoolPartnershipModal(true)
              }}
            >
              Yes
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

ProgramTable.propTypes = propTypes
ProgramTable.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.definedDisciplines(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    grades: globalSelectors.grades(state),
    approaches: globalSelectors.approaches(state),
    isLoading: apiSelectors.isLoading(state, apiActions.fetchProgramTypes),
    languages: globalSelectors.languages(state),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
  fetchGrades: apiActions.fetchGrades,
  fetchOutcomeTypes: apiActions.fetchOutcomeTypes,
  fetchApproaches: apiActions.fetchApproaches,
  fetchLanguages: apiActions.fetchLanguages,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchGrades'),
  onMount('fetchOutcomeTypes'),
  onMount('fetchApproaches'),
  onMount('fetchLanguages'),
  waitFor(['grades', 'outcomeTypes', 'approaches', 'languages'], LoadingSpinner)
)(ProgramTable)
